import React, { useState, useEffect } from 'react';
import { Users, Lock, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';
import TournamentStatistics from './TournamentStatistics';
import './ProfileManagement.css';

const ProfileManagement = ({ user, onUserUpdate, t }) => {
  const [showEdit, setShowEdit] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [editForm, setEditForm] = useState({
    email: '',
    inGameName: ''
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setEditForm({
        email: user.email || '',
        inGameName: user.inGameName || ''
      });
    }
  }, [user]);

  const updateProfile = async () => {
    if (!editForm.email.trim() || !editForm.inGameName.trim()) {
      toast.error(t('pleaseFieldAll', 'Please fill all fields!'));
      return;
    }

    setLoading(true);
    try {
      await axios.put('/api/users/profile', editForm);
      toast.success(t('profileUpdated', 'Profile updated successfully!'));
      onUserUpdate();
      setShowEdit(false);
    } catch (error) {
      const message = error.response?.data?.message || t('failedToUpdateProfile', 'Failed to update profile');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const updatePassword = async () => {
    if (!passwordForm.currentPassword.trim()) {
      toast.error(t('currentPasswordRequired', 'Current password is required!'));
      return;
    }

    if (!passwordForm.newPassword.trim() || passwordForm.newPassword.length < 6) {
      toast.error(t('newPasswordMinLength', 'New password must be at least 6 characters long!'));
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error(t('passwordsDoNotMatch', 'New password and confirmation do not match!'));
      return;
    }

    if (passwordForm.currentPassword === passwordForm.newPassword) {
      toast.error(t('newPasswordMustBeDifferent', 'New password must be different from current password!'));
      return;
    }

    if (passwordForm.newPassword.length > 100) {
      toast.error(t('passwordTooLong', 'Password is too long!'));
      return;
    }

    setPasswordLoading(true);
    
    try {
      const response = await axios.put('/api/users/change-password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      
      toast.success(t('passwordChanged', 'Password changed successfully!'));
      setShowPasswordForm(false);
      resetPasswordForm();
      
    } catch (error) {
      let message = t('failedToChangePassword', 'Failed to change password');
      
      if (error.response?.data?.message) {
        message = error.response.data.message;
      } else if (error.response?.status === 400) {
        message = t('invalidPasswordInfo', 'Invalid password information provided');
      } else if (error.response?.status === 401) {
        message = t('currentPasswordIncorrect', 'Current password is incorrect');
      } else if (error.response?.status === 500) {
        message = t('serverErrorPassword', 'Server error occurred while changing password');
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        message = t('networkErrorPassword', 'Network error - please check your connection');
      }
      
      toast.error(message);
      
    } finally {
      setPasswordLoading(false);
    }
  };

  const resetPasswordForm = () => {
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setShowPasswords({
      current: false,
      new: false,
      confirm: false
    });
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, text: '', color: 'gray' };
    
    let score = 0;
    if (password.length >= 6) score++;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 2) return { strength: score, text: t('weak', 'Weak'), color: 'weak' };
    if (score <= 4) return { strength: score, text: t('medium', 'Medium'), color: 'medium' };
    return { strength: score, text: t('strong', 'Strong'), color: 'strong' };
  };

  const passwordStrength = getPasswordStrength(passwordForm.newPassword);

  const testPasswordValidation = async () => {
    if (!passwordForm.currentPassword) {
      toast.error(t('enterCurrentPasswordFirst', 'Enter current password first'));
      return;
    }
    
    try {
      const response = await axios.post('/api/users/validate-password', {
        password: passwordForm.currentPassword
      });
      
      if (response.data.valid) {
        toast.success(t('currentPasswordCorrect', 'Current password is correct!'));
      } else {
        toast.error(t('currentPasswordIncorrect', 'Current password is incorrect!'));
      }
    } catch (error) {
      console.error('Password validation error:', error);
      toast.error(t('failedToValidatePassword', 'Failed to validate password'));
    }
  };

  const getRoleDisplay = (role) => {
    const roleMap = {
      'main_admin': t('mainAdmin', 'Main Admin'),
      'supervisor': t('supervisor', 'Supervisor'),
      'approver': t('approver', 'Approver'),
      'player': t('player', 'Player')
    };
    return roleMap[role] || roleMap['player'];
  };

  return (
    <div className="profile-management">
      {/* Profile Information */}
      <div className="profile-section">
        <h3 className="profile-section-title">
          <Users className="profile-section-icon" />
          {t('profileManagement', 'Profile Management')}
        </h3>
        
        <div className="profile-info">
          <div className="profile-info-grid">
            <div className="profile-info-column">
              <div className="profile-info-item">
                <span className="profile-info-label">{t('username', 'Username')}:</span>
                <span className="profile-info-value">{user.username}</span>
              </div>
              <div className="profile-info-item">
                <span className="profile-info-label">{t('email', 'Email')}:</span>
                <span className="profile-info-value">{user.email}</span>
              </div>
              <div className="profile-info-item">
                <span className="profile-info-label">{t('inGameName', 'In-Game Name')}:</span>
                <span className="profile-info-value">{user.inGameName}</span>
              </div>
            </div>
            
            <div className="profile-info-column">
              <div className="profile-info-item">
                <span className="profile-info-label">{t('points', 'Points')}:</span>
                <span className="profile-info-value profile-points">{user.points}</span>
              </div>
              <div className="profile-info-item">
                <span className="profile-info-label">{t('role', 'Role')}:</span>
                <span className={`profile-info-value profile-role profile-role-${user.role}`}>
                  {getRoleDisplay(user.role)}
                </span>
              </div>
              <div className="profile-info-item">
                <span className="profile-info-label">{t('memberSince', 'Member Since')}:</span>
                <span className="profile-info-value profile-member-since">
                  {new Date(user.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="profile-actions-buttons">
          <button
            onClick={() => setShowEdit(true)}
            className="profile-action-btn profile-edit-btn"
          >
            {t('editProfile', 'Edit Profile')}
          </button>
          <button
            onClick={() => setShowPasswordForm(true)}
            className="profile-action-btn profile-password-btn"
          >
            <Lock className="profile-action-icon" />
            {t('changePassword', 'Change Password')}
          </button>
        </div>
      </div>

      {/* Tournament Statistics Component */}
      <TournamentStatistics user={user} t={t} />

      {/* Edit Profile Modal */}
      {showEdit && (
        <div className="profile-modal-overlay">
          <div className="profile-modal">
            <h3>
              <Users className="profile-modal-icon" />
              {t('editProfile', 'Edit Profile')}
            </h3>
            
            <div className="profile-form">
              <div className="profile-field">
                <label className="profile-label">
                  {t('email', 'Email')}
                </label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                  className="profile-input"
                  disabled={loading}
                />
              </div>
              
              <div className="profile-field">
                <label className="profile-label">
                  {t('inGameName', 'In-Game Name')}
                </label>
                <input
                  type="text"
                  value={editForm.inGameName}
                  onChange={(e) => setEditForm({...editForm, inGameName: e.target.value})}
                  className="profile-input"
                  disabled={loading}
                />
                <p className="profile-warning">
                  {t('makesSureThisMatches', '⚠️ Make sure this matches your exact CoD Mobile in-game name!')}
                </p>
              </div>

              <div className="profile-actions">
                <button
                  onClick={updateProfile}
                  disabled={loading}
                  className="profile-btn profile-btn-primary"
                >
                  {loading ? t('updating', 'Updating...') : t('updateProfile', 'Update Profile')}
                </button>
                <button
                  onClick={() => setShowEdit(false)}
                  disabled={loading}
                  className="profile-btn profile-btn-cancel"
                >
                  {t('cancel', 'Cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordForm && (
        <div className="profile-modal-overlay">
          <div className="profile-modal password-modal">
            <h3>
              <Lock className="profile-modal-icon" />
              {t('changePassword', 'Change Password')}
            </h3>
            
            <div className="profile-form">
              {/* Current Password */}
              <div className="profile-field">
                <label className="profile-label">
                  {t('currentPassword', 'Current Password')}
                </label>
                <div className="profile-password-field">
                  <input
                    type={showPasswords.current ? "text" : "password"}
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                    className="profile-input"
                    disabled={passwordLoading}
                    placeholder={t('enterCurrentPassword', 'Enter your current password')}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('current')}
                    className="profile-password-toggle"
                  >
                    {showPasswords.current ? <EyeOff className="profile-toggle-icon" /> : <Eye className="profile-toggle-icon" />}
                  </button>
                </div>
                
                {process.env.NODE_ENV === 'development' && (
                  <button
                    type="button"
                    onClick={testPasswordValidation}
                    className="profile-test-password-btn"
                  >
                    {t('testCurrentPassword', 'Test Current Password')}
                  </button>
                )}
              </div>

              {/* New Password */}
              <div className="profile-field">
                <label className="profile-label">
                  {t('newPassword', 'New Password')}
                </label>
                <div className="profile-password-field">
                  <input
                    type={showPasswords.new ? "text" : "password"}
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                    className="profile-input"
                    disabled={passwordLoading}
                    placeholder={t('enterNewPassword', 'Enter new password (min 6 characters)')}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('new')}
                    className="profile-password-toggle"
                  >
                    {showPasswords.new ? <EyeOff className="profile-toggle-icon" /> : <Eye className="profile-toggle-icon" />}
                  </button>
                </div>
                
                {passwordForm.newPassword && (
                  <div className="profile-password-strength">
                    <div className="profile-strength-bar">
                      <div className="profile-strength-progress">
                        <div 
                          className={`profile-strength-fill ${passwordStrength.color}`}
                          style={{ width: `${(passwordStrength.strength / 6) * 100}%` }}
                        ></div>
                      </div>
                      <span className={`profile-strength-text ${passwordStrength.color}`}>
                        {passwordStrength.text}
                      </span>
                    </div>
                    <p className="profile-strength-hint">
                      {t('useUppercaseLowercase', 'Use uppercase, lowercase, numbers, and symbols for a stronger password')}
                    </p>
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div className="profile-field">
                <label className="profile-label">
                  {t('confirmNewPassword', 'Confirm New Password')}
                </label>
                <div className="profile-password-field">
                  <input
                    type={showPasswords.confirm ? "text" : "password"}
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                    className="profile-input"
                    disabled={passwordLoading}
                    placeholder={t('confirmYourNewPassword', 'Confirm your new password')}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirm')}
                    className="profile-password-toggle"
                  >
                    {showPasswords.confirm ? <EyeOff className="profile-toggle-icon" /> : <Eye className="profile-toggle-icon" />}
                  </button>
                </div>
                
                {passwordForm.confirmPassword && (
                  <p className={`profile-password-match ${
                    passwordForm.newPassword === passwordForm.confirmPassword 
                      ? 'match' 
                      : 'no-match'
                  }`}>
                    {passwordForm.newPassword === passwordForm.confirmPassword 
                      ? t('passwordsMatch', '✓ Passwords match') 
                      : t('passwordsDoNotMatch', '✗ Passwords do not match')
                    }
                  </p>
                )}
              </div>

              {/* Security Notice */}
              <div className="profile-security-notice">
                <p>
                  <strong>{t('securityTips', 'Security Tips')}:</strong><br/>
                  • {t('useAtLeast8Characters', 'Use at least 8 characters')}<br/>
                  • {t('includeUppercaseLowercase', 'Include uppercase and lowercase letters')}<br/>
                  • {t('addNumbersSpecialChars', 'Add numbers and special characters')}<br/>
                  • {t('dontUsePersonalInfo', 'Don\'t use personal information')}
                </p>
              </div>

              <div className="profile-actions">
                <button
                  onClick={updatePassword}
                  disabled={passwordLoading || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword || passwordForm.newPassword !== passwordForm.confirmPassword}
                  className="profile-btn profile-btn-password"
                >
                  {passwordLoading ? t('changing', 'Changing...') : t('changePassword', 'Change Password')}
                </button>
                <button
                  onClick={() => {
                    setShowPasswordForm(false);
                    resetPasswordForm();
                  }}
                  disabled={passwordLoading}
                  className="profile-btn profile-btn-cancel"
                >
                  {t('cancel', 'Cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileManagement;